import { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import {
  Scale, Search, Filter, MessageCircle, Clock, CheckCircle,
  XCircle, AlertCircle, ArrowUpRight, Send, Loader2,
  BarChart3, Inbox, ChevronRight, RefreshCw, User
} from 'lucide-react'
import { disputeService, Dispute, DisputeMessage, DisputeStats } from '../services/disputeService'

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: any }> = {
  open: { label: 'Ouvert', color: 'text-blue-700', bgColor: 'bg-blue-100', icon: AlertCircle },
  under_review: { label: 'En cours', color: 'text-yellow-700', bgColor: 'bg-yellow-100', icon: Clock },
  resolved: { label: 'Résolu', color: 'text-green-700', bgColor: 'bg-green-100', icon: CheckCircle },
  rejected: { label: 'Rejeté', color: 'text-red-700', bgColor: 'bg-red-100', icon: XCircle },
  escalated: { label: 'Escaladé', color: 'text-purple-700', bgColor: 'bg-purple-100', icon: ArrowUpRight },
}

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  low: { label: 'Basse', color: 'text-green-600' },
  medium: { label: 'Moyenne', color: 'text-yellow-600' },
  high: { label: 'Haute', color: 'text-red-600' },
}

const TABS = [
  { id: 'inbox', label: 'Boîte de réception', icon: Inbox },
  { id: 'analytics', label: 'Analytique', icon: BarChart3 },
]

const DisputeManagementPage = () => {
  const [activeTab, setActiveTab] = useState('inbox')
  const [disputes, setDisputes] = useState<Dispute[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [priorityFilter, setPriorityFilter] = useState<string>('all')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState<DisputeStats | null>(null)
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null)
  const [messages, setMessages] = useState<DisputeMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  const [resolution, setResolution] = useState('')
  const [resolutionStatus, setResolutionStatus] = useState<'resolved' | 'rejected'>('resolved')
  const [processingResolution, setProcessingResolution] = useState(false)
  const limit = 20

  const loadDisputes = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { page, limit }
      if (statusFilter !== 'all') params.status = statusFilter
      if (priorityFilter !== 'all') params.priority = priorityFilter
      if (search) params.search = search
      const res = await disputeService.getDisputes(params)
      if (res.success) {
        setDisputes(res.data)
        setTotal(res.total)
      }
    } catch {
      toast.error('Erreur lors du chargement des litiges')
    } finally {
      setLoading(false)
    }
  }, [page, statusFilter, priorityFilter, search])

  const loadStats = useCallback(async () => {
    try {
      const res = await disputeService.getStats()
      if (res.success) {
        setStats(res.data)
      }
    } catch {
      // Silently fail
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'inbox') {
      loadDisputes()
    } else {
      loadStats()
    }
  }, [activeTab, loadDisputes, loadStats])

  const loadMessages = useCallback(async (disputeId: string) => {
    try {
      const res = await disputeService.getMessages(disputeId)
      if (res.success) {
        setMessages(res.data)
      }
    } catch {
      // Silently fail
    }
  }, [])

  const openDispute = async (dispute: Dispute) => {
    setSelectedDispute(dispute)
    setMessages([])
    setResolution('')
    if (dispute.status === 'open' || dispute.status === 'under_review' || dispute.status === 'escalated') {
      await loadMessages(dispute.dispute_id)
    }
  }

  const handleSendMessage = async () => {
    if (!selectedDispute || !newMessage.trim()) return
    setSendingMessage(true)
    try {
      const res = await disputeService.addMessage(selectedDispute.dispute_id, newMessage.trim())
      if (res.success) {
        setMessages((prev) => [...prev, res.data])
        setNewMessage('')
        toast.success('Message envoyé')
      }
    } catch {
      toast.error('Erreur d\'envoi')
    } finally {
      setSendingMessage(false)
    }
  }

  const handleResolve = async () => {
    if (!selectedDispute) return
    setProcessingResolution(true)
    try {
      const res = await disputeService.updateDisputeStatus(
        selectedDispute.dispute_id,
        resolutionStatus,
        resolution || undefined
      )
      if (res.success) {
        toast.success(`Litige ${resolutionStatus === 'resolved' ? 'résolu' : 'rejeté'}`)
        setSelectedDispute(res.data)
        setDisputes((prev) =>
          prev.map((d) => (d.dispute_id === res.data.dispute_id ? res.data : d))
        )
        setResolution('')
      }
    } catch {
      toast.error('Erreur de mise à jour')
    } finally {
      setProcessingResolution(false)
    }
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Litiges</h1>
        <p className="text-sm text-gray-500 mt-1">
          Gérez les litiges clients et suivez leur résolution
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        {TABS.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {activeTab === 'inbox' ? (
        <div className="flex gap-6">
          {/* Left panel - Disputes list */}
          <div className="flex-1 min-w-0">
            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher un litige..."
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-lg border-gray-300 text-sm"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="open">Ouvert</option>
                  <option value="under_review">En cours</option>
                  <option value="resolved">Résolu</option>
                  <option value="rejected">Rejeté</option>
                  <option value="escalated">Escaladé</option>
                </select>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="rounded-lg border-gray-300 text-sm"
                >
                  <option value="all">Toutes priorités</option>
                  <option value="high">Haute</option>
                  <option value="medium">Moyenne</option>
                  <option value="low">Basse</option>
                </select>
              </div>
            </div>

            {/* List */}
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
              </div>
            ) : disputes.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
                <Scale className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucun litige trouvé</p>
              </div>
            ) : (
              <div className="space-y-2">
                {disputes.map((dispute) => {
                  const statusConfig = STATUS_CONFIG[dispute.status] || STATUS_CONFIG.open
                  const priorityConfig = PRIORITY_CONFIG[dispute.priority] || PRIORITY_CONFIG.medium
                  const StatusIcon = statusConfig.icon
                  return (
                    <button
                      key={dispute.dispute_id}
                      onClick={() => openDispute(dispute)}
                      className={`w-full text-left bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-all ${
                        selectedDispute?.dispute_id === dispute.dispute_id
                          ? 'border-primary-500 ring-1 ring-primary-500'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-sm font-semibold text-gray-900 truncate">
                              {dispute.subject}
                            </h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </span>
                            <span className={`text-xs font-medium ${priorityConfig.color}`}>
                              {priorityConfig.label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 line-clamp-1">{dispute.description}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                            <span>#{dispute.dispute_id.slice(0, 8)}</span>
                            {dispute.user && (
                              <span className="flex items-center">
                                <User className="w-3 h-3 mr-1" />
                                {dispute.user.first_name || 'Utilisateur'}
                              </span>
                            )}
                            <span>{new Date(dispute.created_at).toLocaleDateString()}</span>
                            {dispute.messages && dispute.messages.length > 0 && (
                              <span className="flex items-center">
                                <MessageCircle className="w-3 h-3 mr-1" />
                                {dispute.messages.length}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300 ml-4 flex-shrink-0" />
                      </div>
                    </button>
                  )
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-500">Page {page}/{totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>

          {/* Right panel - Dispute detail */}
          {selectedDispute && (
            <div className="w-96 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Détail du litige</h3>
                  <button
                    onClick={() => setSelectedDispute(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>

                {/* Dispute info */}
                <div className="space-y-3 mb-4 text-sm">
                  <div>
                    <span className="text-gray-500">Sujet:</span>
                    <p className="font-medium text-gray-900">{selectedDispute.subject}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Description:</span>
                    <p className="text-gray-700">{selectedDispute.description}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Priorité:</span>
                    <p className={`font-medium ${PRIORITY_CONFIG[selectedDispute.priority]?.color}`}>
                      {PRIORITY_CONFIG[selectedDispute.priority]?.label}
                    </p>
                  </div>
                  {selectedDispute.user && (
                    <div>
                      <span className="text-gray-500">Client:</span>
                      <p className="font-medium text-gray-900">
                        {selectedDispute.user.first_name} {selectedDispute.user.last_name}
                        <span className="text-gray-400 text-xs ml-2">{selectedDispute.user.email}</span>
                      </p>
                    </div>
                  )}
                  {selectedDispute.reservation && (
                    <div>
                      <span className="text-gray-500">Commande:</span>
                      <p className="font-medium text-gray-900">
                        {selectedDispute.reservation.item_name} - {selectedDispute.reservation.merchant_name}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Créé le:</span>
                    <p className="text-gray-700">{new Date(selectedDispute.created_at).toLocaleString()}</p>
                  </div>
                </div>

                {/* Messages */}
                {(selectedDispute.status === 'open' || selectedDispute.status === 'under_review' || selectedDispute.status === 'escalated') && (
                  <>
                    <div className="border-t border-gray-200 pt-4 mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Messages</h4>
                      <div className="space-y-3 max-h-60 overflow-y-auto mb-3">
                        {messages.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center">Aucun message</p>
                        ) : (
                          messages.map((msg) => (
                            <div
                              key={msg.message_id}
                              className={`p-3 rounded-lg text-sm ${
                                msg.is_admin
                                  ? 'bg-primary-50 border border-primary-200 ml-4'
                                  : 'bg-gray-50 border border-gray-200 mr-4'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-gray-500">
                                  {msg.sender_name || (msg.is_admin ? 'Support' : 'Client')}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {new Date(msg.created_at).toLocaleTimeString()}
                                </span>
                              </div>
                              <p className="text-gray-700">{msg.message}</p>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Votre message..."
                          className="flex-1 rounded-lg border-gray-300 text-sm"
                          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                        />
                        <button
                          onClick={handleSendMessage}
                          disabled={sendingMessage || !newMessage.trim()}
                          className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                        >
                          {sendingMessage ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Resolution */}
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Résolution</h4>
                      <div className="flex gap-2 mb-3">
                        <button
                          onClick={() => setResolutionStatus('resolved')}
                          className={`flex-1 px-3 py-1.5 text-sm rounded-lg border ${
                            resolutionStatus === 'resolved'
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-gray-300 text-gray-500'
                          }`}
                        >
                          Résoudre
                        </button>
                        <button
                          onClick={() => setResolutionStatus('rejected')}
                          className={`flex-1 px-3 py-1.5 text-sm rounded-lg border ${
                            resolutionStatus === 'rejected'
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-gray-300 text-gray-500'
                          }`}
                        >
                          Rejeter
                        </button>
                      </div>
                      <textarea
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        placeholder="Notes de résolution (optionnel)..."
                        rows={2}
                        className="w-full rounded-lg border-gray-300 text-sm resize-none mb-3"
                      />
                      <button
                        onClick={handleResolve}
                        disabled={processingResolution}
                        className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                      >
                        {processingResolution ? (
                          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                        ) : (
                          `Confirmer le ${resolutionStatus === 'resolved' ? 'résolution' : 'rejet'}`
                        )}
                      </button>
                    </div>
                  </>
                )}

                {/* Resolved/Rejected info */}
                {(selectedDispute.status === 'resolved' || selectedDispute.status === 'rejected') && (
                  <div className="border-t border-gray-200 pt-4">
                    <div className={`p-3 rounded-lg ${
                      selectedDispute.status === 'resolved' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}>
                      <p className={`text-sm font-medium ${
                        selectedDispute.status === 'resolved' ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {selectedDispute.status === 'resolved' ? '✓ Résolu' : '✗ Rejeté'}
                      </p>
                      {selectedDispute.resolution && (
                        <p className="text-sm text-gray-600 mt-1">{selectedDispute.resolution}</p>
                      )}
                      {selectedDispute.resolved_at && (
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(selectedDispute.resolved_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Analytics Tab */
        <div>
          {!stats ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </div>
                <div className="bg-blue-50 rounded-xl shadow-sm border border-blue-200 p-4">
                  <p className="text-2xl font-bold text-blue-700">{stats.open}</p>
                  <p className="text-sm text-blue-600">Ouverts</p>
                </div>
                <div className="bg-yellow-50 rounded-xl shadow-sm border border-yellow-200 p-4">
                  <p className="text-2xl font-bold text-yellow-700">{stats.under_review}</p>
                  <p className="text-sm text-yellow-600">En cours</p>
                </div>
                <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-4">
                  <p className="text-2xl font-bold text-green-700">{stats.resolved}</p>
                  <p className="text-sm text-green-600">Résolus</p>
                </div>
                <div className="bg-red-50 rounded-xl shadow-sm border border-red-200 p-4">
                  <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
                  <p className="text-sm text-red-600">Rejetés</p>
                </div>
                <div className="bg-purple-50 rounded-xl shadow-sm border border-purple-200 p-4">
                  <p className="text-2xl font-bold text-purple-700">{stats.escalated}</p>
                  <p className="text-sm text-purple-600">Escaladés</p>
                </div>
              </div>

              {/* Avg resolution time */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Temps moyen de résolution</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.avg_resolution_time_hours.toFixed(1)} heures
                </p>
              </div>

              {/* By priority */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Par priorité</h3>
                <div className="space-y-2">
                  {stats.by_priority.map((item) => {
                    const maxCount = Math.max(...stats.by_priority.map((p) => p.count))
                    const pct = maxCount > 0 ? (item.count / maxCount) * 100 : 0
                    return (
                      <div key={item.priority} className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-20">
                          {PRIORITY_CONFIG[item.priority]?.label || item.priority}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              item.priority === 'high' ? 'bg-red-500' :
                              item.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700 w-8 text-right">{item.count}</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* By subject */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Par sujet</h3>
                <div className="space-y-2">
                  {stats.by_subject.map((item) => {
                    const maxCount = Math.max(...stats.by_subject.map((s) => s.count))
                    const pct = maxCount > 0 ? (item.count / maxCount) * 100 : 0
                    return (
                      <div key={item.subject} className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 flex-1">{item.subject}</span>
                        <div className="w-32 bg-gray-100 rounded-full h-2">
                          <div
                            className="h-2 rounded-full bg-primary-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700 w-8 text-right">{item.count}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DisputeManagementPage
